/**
 * MOCK DATA — deterministic pseudo-random helpers.
 *
 * A fixed seed keeps the fixtures byte-stable across reloads, which matters:
 * a dashboard that reshuffles its numbers on every refresh is untestable and
 * makes it impossible to tell a real update from noise.
 *
 * See `src/mock/banner.ts` for the rules this directory obeys.
 */

/** mulberry32 — small, fast, deterministic. Not cryptographic and not meant to be. */
export function createRng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Pick from a list without tripping over `noUncheckedIndexedAccess`. */
export function pick<T>(items: readonly T[], index: number): T {
  if (items.length === 0) throw new Error('pick() called with an empty list');
  const item = items[((index % items.length) + items.length) % items.length];
  if (item === undefined) throw new Error('pick() produced no value');
  return item;
}

export function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

/** Deterministic identifier, e.g. `trade-0007`. Obviously not a real UUID. */
export function mockId(prefix: string, index: number, width = 4): string {
  return `${prefix}-${String(index).padStart(width, '0')}`;
}

export function mockUuid(prefix: string, index: number): string {
  const hex = (index * 0x9e3779b1).toString(16).padStart(8, '0').slice(-8);
  return `${prefix}${hex}-0000-4000-8000-${hex}${hex}`.slice(0, 36);
}

/** ISO timestamp `offsetMs` before `base`, in whole seconds (stable output). */
export function isoBefore(base: number, offsetMs: number): string {
  return new Date(Math.round((base - offsetMs) / 1000) * 1000).toISOString();
}

export function isoAt(base: number, offsetMs: number): string {
  return new Date(Math.round((base + offsetMs) / 1000) * 1000).toISOString();
}

/** `YYYY-MM-DD` in UTC — session dates on the wire are broker-local strings. */
export function sessionDate(base: number): string {
  return new Date(base).toISOString().slice(0, 10);
}
